import React, { useCallback, useEffect, useRef } from 'react'
import QRCode from 'qrcode.react'
import { Modal, Toast } from 'antd-mobile-v5'
import { FormattedMessage, useIntl } from 'react-intl'
import { FUIOU_WPOS_PAY_TYPE, isApplicationPaymentPaid, PAY_TYPE_DETAIL_FN } from './utils'
import { useFuiouPaymentPolling } from '../../services/useFuiouPaymentPolling'

// PC 端 (wpos 通道) Fuiou 二维码弹窗
const POLL_TIMEOUT_MS = 15 * 60 * 1000

// 渠道二维码边框色（对齐 china-club-web wechat-native-payment.vue：微信绿 / 支付宝蓝）
const CHANNEL_BORDER_COLOR = {
  WECHAT: '#20a653',
  ALIPAY: '#1677ff',
}

const FuiouQrPopup = ({
  visible,
  url,
  price,
  currency,
  discountPrice,
  onClose,
  onPaymentSuccess,
  payApplicationType,
  code,
  payType,
  checkStatus,
  titleId,
  timeoutMs,
  timeoutMessageId,
  onCancelOrder,
}) => {
  const { formatMessage } = useIntl()
  const isAlipay = payType === FUIOU_WPOS_PAY_TYPE.ALIPAY
  const hasChannel = payType === FUIOU_WPOS_PAY_TYPE.WECHAT || isAlipay
  // 金额展示（CHNA-2618 后端补出 price/currency）：只展示不计算，price 已是两位小数字符串；
  // 0 元（优惠券全额抵扣）为合法状态照常展示。币种缺省按富友通道恒 CNY 处理
  const hasPrice = price != null && price !== ''
  const isCny = !currency || currency === 'CNY'
  const amountText = hasPrice ? (isCny ? `¥${price}` : `${price} ${currency}`) : null
  // 优惠折扣行（可选，eCard 本地券下单响应 couponPrice）：服务端权威金额，只展示不计算
  const hasDiscount = discountPrice != null && discountPrice !== ''
  const discountText = hasDiscount ? (isCny ? `¥${discountPrice}` : `${discountPrice} ${currency}`) : null
  // titleId 显式传入时优先（如续费页固定标题）；否则按渠道展示微信/支付宝标题
  const resolvedTitleId =
    titleId || (hasChannel ? (isAlipay ? 'fuiou-qr-title-alipay' : 'fuiou-qr-title-wechat') : 'fuiou-qr-title')
  const hintId = hasChannel ? (isAlipay ? 'fuiou-qr-hint-alipay' : 'fuiou-qr-hint-wechat') : 'fuiou-qr-hint'
  // onClose/onPaymentSuccess/onCancelOrder 由父组件内联传入，引用不稳定；用 ref 持有避免 effect 依赖抖动重置轮询
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const onPaymentSuccessRef = useRef(onPaymentSuccess)
  onPaymentSuccessRef.current = onPaymentSuccess
  const onCancelOrderRef = useRef(onCancelOrder)
  onCancelOrderRef.current = onCancelOrder
  // 弹窗内容容器：afterShow 时聚焦，把焦点移入弹窗支持键盘用户
  const contentRef = useRef(null)

  const fetchDetail = useCallback(async () => {
    const fn = PAY_TYPE_DETAIL_FN[payApplicationType]
    // 未知申请类型没有对应查询端点：主动 throw 走 useFuiouPaymentPolling 的连续失败计数，
    // 弹可见错误提示，而不是静默返回 false 轮满 15 分钟超时
    if (!fn) throw new Error(`unknown payApplicationType: ${payApplicationType}`)
    const result = await fn(code)
    // getData 返回 { data: {...}, error? } 包装；查询失败时主动 throw，
    // 让 useFuiouPaymentPolling 的连续失败计数生效（否则会静默轮满超时）
    if (result && result.error) throw result.error
    const data = result && result.data
    return isApplicationPaymentPaid(data)
  }, [payApplicationType, code])

  const handleSuccess = useCallback(() => {
    if (onPaymentSuccessRef.current) onPaymentSuccessRef.current()
  }, [])

  // 收起弹窗 ≠ 取消订单：这里只收起，不关单。
  // 关单不可逆（CLOSED 单后端 getPaymentUrl 拒绝重付、C 端无恢复入口，只能重新下单或找客服），
  // 而「关闭二维码 / 返回上一页 / 弹窗超时」都不代表用户想放弃这张订单——此前这三条路径都会
  // fire-and-forget 关单，用户误触关闭、或只是想换个支付方式，就直接失去了这张单。
  // 订单改为留在 WAIT_PAY：可随时重新进入继续支付，超时未付由后端定时关单兜底。
  const handleClose = useCallback(() => {
    if (onCloseRef.current) onCloseRef.current()
  }, [])

  // 超时是"查不到结果"而非"支付失败"：用户可能已付款（如支付确认延迟），
  // 文案必须中性、绝不用失败图标——失败语义会诱导用户重新下单重复付款。
  // 超时只收起弹窗，不关单：订单仍是 WAIT_PAY，重新打开还能继续付
  const handleTimeout = useCallback(() => {
    Toast.show({ content: formatMessage({ id: timeoutMessageId || 'fuiou-qr-timeout' }) })
    handleClose()
  }, [formatMessage, timeoutMessageId, handleClose])

  // 显式「取消订单」——本组件唯一的关单入口。按产品要求不做二次确认：按钮文案本身就是
  // "取消订单"这一显式动作，点下去直接关单。
  // 关单失败静默收起（不提示"已取消"）：后端幂等，失败时订单仍是 WAIT_PAY，用户可再次进入支付
  const handleCancelOrder = useCallback(async () => {
    if (!onCancelOrderRef.current) return
    let cancelled = false
    try {
      await onCancelOrderRef.current()
      cancelled = true
    } catch {
      // 静默：关单失败不代表订单已取消，直接收起让用户可重试
    }
    if (cancelled) {
      Toast.show({ content: formatMessage({ id: 'fuiou-qr-order-cancelled' }) })
    }
    handleClose()
  }, [formatMessage, handleClose])

  // 连续轮询失败（网络中断）只提示"仍在确认中"，不关闭弹窗、不用失败图标：
  // 轮询由 hook 继续重试，直到超时才由 handleTimeout 收尾
  const handlePollingError = useCallback(() => {
    Toast.show({ content: formatMessage({ id: 'fuiou-poll-confirming' }) })
  }, [formatMessage])

  const { startPolling } = useFuiouPaymentPolling({
    // 外部传入 checkStatus（如教材订单按 orderId 查状态）时优先使用；
    // 否则按 payApplicationType 走 PAY_TYPE_DETAIL_FN 的轻量 paymentStatus 端点
    checkStatus: checkStatus || fetchDetail,
    isActive: visible,
    onSuccess: handleSuccess,
    onTimeout: handleTimeout,
    onPollingError: handlePollingError,
    // timeoutMs 可定制（续费页保持 5 分钟时限），默认 15 分钟
    timeoutMs: timeoutMs || POLL_TIMEOUT_MS,
  })

  useEffect(() => {
    if (visible && url) {
      startPolling()
    }
  }, [visible, url, startPolling])

  return (
    <Modal
      visible={visible}
      title={formatMessage({ id: resolvedTitleId })}
      // 关单要走二次确认（可能还要等接口），故不随点击自动收起，由各 action 自己决定何时 handleClose
      closeOnAction={false}
      afterShow={() => {
        // 弹窗打开后把焦点移入内容容器，支持键盘用户 Tab 到达关闭按钮
        if (contentRef.current) {
          contentRef.current.focus()
        }
      }}
      onClose={handleClose}
      content={
        <div ref={contentRef} tabIndex={-1} style={{ textAlign: 'center', padding: '8px 0', outline: 'none' }}>
          {amountText && (
            <div style={{ marginBottom: 12, fontSize: 22, fontWeight: 'bold', color: '#ff3141' }}>
              <FormattedMessage id="fuiou-qr-amount" values={{ amount: amountText }} />
            </div>
          )}
          {discountText && (
            <div style={{ marginTop: -6, marginBottom: 12, fontSize: 14, color: '#666' }}>
              <FormattedMessage id="ecard-coupon-discounted" values={{ amount: discountText }} />
            </div>
          )}
          {url && (
            <div
              style={{
                display: 'inline-block',
                padding: 6,
                border: `3px solid ${hasChannel ? CHANNEL_BORDER_COLOR[payType] : '#d7dde2'}`,
                borderRadius: 8,
                lineHeight: 0,
              }}
            >
              <QRCode value={url} size={240} includeMargin />
            </div>
          )}
          <div style={{ marginTop: 16, fontSize: 14, color: '#666' }}>
            <FormattedMessage id={hintId} />
          </div>
        </div>
      }
      actions={[
        // 「取消订单」只在调用方提供了关单回调时渲染（如共享 PaymentBtn 的申请类支付没有
        // 对应 closeOrder 端点，传 undefined 就不给这个按钮，避免点了无处可去）
        ...(onCancelOrder
          ? [
              {
                key: 'cancelOrder',
                text: formatMessage({ id: 'fuiou-qr-cancel-order' }),
                danger: true,
                onClick: handleCancelOrder,
              },
            ]
          : []),
        {
          key: 'close',
          text: formatMessage({ id: 'common-close' }),
          // 关闭仅收起弹窗：订单保持 WAIT_PAY 可继续支付（关单只能走上面的显式取消）
          onClick: handleClose,
        },
      ]}
    />
  )
}

export default FuiouQrPopup
